import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Smile } from 'lucide-react';
import { apiService, Procedure, LocalizedString, ProviderRole, resolveLocalized } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const ROLE_OPTIONS: Array<{ value: ProviderRole; labelKey: string }> = [
  { value: 'dentist', labelKey: 'procedures.roleDentist' },
  { value: 'hygienist', labelKey: 'procedures.roleHygienist' },
];

const PRESET_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280', '#F97316', '#14B8A6', '#FBBF24', '#A78BFA', '#60A5FA', '#DC2626'];
const LOCALES: Array<{ code: keyof LocalizedString; label: string }> = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
];

const emptyLocalized: LocalizedString = { de: '', en: '', es: '', ru: '' };

const emptyProcedure: Partial<Procedure> = {
  key: '',
  name: { ...emptyLocalized },
  description: { ...emptyLocalized },
  duration: 30,
  color: '#3B82F6',
  required_roles: ['dentist'],
  is_active: true,
  bookable: true,
};

const toLocalized = (v: any): LocalizedString => {
  if (!v) return { ...emptyLocalized };
  if (typeof v === 'string') return { ...emptyLocalized, de: v };
  return { de: v.de || '', en: v.en || '', es: v.es || '', ru: v.ru || '' };
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const ProceduresPage: React.FC = () => {
  const { t } = useTranslation();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  type EditState = {
    id?: number;
    key?: string;
    name: LocalizedString;
    description: LocalizedString;
    duration?: number;
    color?: string;
    required_roles: ProviderRole[];
    is_active?: boolean;
    bookable?: boolean;
  };
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeLocale, setActiveLocale] = useState<keyof LocalizedString>('de');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getProcedures(false);
      setProcedures(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || t('procedures.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p?: Procedure) => {
    if (p) {
      setEditing({
        id: p.id,
        key: p.key,
        name: toLocalized(p.name),
        description: toLocalized(p.description),
        duration: p.duration,
        color: p.color,
        required_roles: (p.required_roles && p.required_roles.length > 0) ? [...p.required_roles] : ['dentist'],
        is_active: p.is_active,
        bookable: p.bookable,
      });
    } else {
      setEditing({ ...emptyProcedure } as EditState);
    }
    setActiveLocale('de');
  };

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name.de) {
      setError(t('procedures.errorNameRequired'));
      return;
    }
    if (!editing.required_roles || editing.required_roles.length === 0) {
      setError(t('procedures.errorRoleRequired'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload: Partial<Procedure> = {
        key: editing.key,
        name: editing.name,
        description: editing.description,
        duration: editing.duration,
        color: editing.color,
        required_roles: editing.required_roles,
        is_active: editing.is_active,
        bookable: editing.bookable,
      };
      if (editing.id) {
        await apiService.updateProcedure(editing.id, payload);
      } else {
        await apiService.createProcedure(payload);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('procedures.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: Procedure) => {
    if (!window.confirm(t('procedures.confirmDelete', { name: resolveLocalized(p.name) }))) return;
    try {
      await apiService.deleteProcedure(p.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || t('procedures.errorDelete'));
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('procedures.eyebrow')}
        title={t('procedures.title')}
        description={t('procedures.pageDescription')}
        actions={
          <button
            onClick={() => startEdit()}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
          >
            <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
            {t('procedures.newProcedure')}
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">{t('common.loading')}</div>
        ) : procedures.length === 0 ? (
          <EmptyState
            icon={Smile}
            title={t('procedures.emptyTitle')}
            description={t('procedures.emptyDescription')}
            action={
              <button
                onClick={() => startEdit()}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
                {t('procedures.emptyAction')}
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-brand-border">
              <thead className="bg-brand-surface-2/60">
                <tr>
                  <Th>{t('procedures.colNameDe')}</Th>
                  <Th>{t('procedures.colKey')}</Th>
                  <Th>{t('procedures.colDuration')}</Th>
                  <Th>{t('procedures.colRequired')}</Th>
                  <Th>{t('procedures.colColor')}</Th>
                  <Th>{t('procedures.colBookable')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="right"><span className="sr-only">{t('common.actions')}</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {procedures.map(p => (
                  <tr key={p.id} className={`hover:bg-brand-surface-2/60 transition-colors ${p.is_active ? '' : 'opacity-60'}`}>
                    <td className="px-4 py-3 text-sm font-medium text-brand-text max-w-md">{resolveLocalized(p.name, 'de')}</td>
                    <td className="px-4 py-3 text-xs text-brand-text-muted font-mono">{p.key || '—'}</td>
                    <td className="px-4 py-3 text-sm text-brand-text whitespace-nowrap tabular-nums">{p.duration} min</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(p.required_roles && p.required_roles.length > 0 ? p.required_roles : ['dentist']).map(r => {
                          const opt = ROLE_OPTIONS.find(o => o.value === r);
                          return (
                          <StatusPill key={r} tone={r === 'hygienist' ? 'accent' : 'info'}>
                            {opt ? t(opt.labelKey) : r}
                          </StatusPill>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className="inline-block w-5 h-5 rounded ring-1 ring-brand-border"
                        style={{ backgroundColor: p.color }}
                        title={p.color}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.bookable !== false
                        ? <StatusPill tone="info">{t('procedures.public')}</StatusPill>
                        : <StatusPill tone="neutral">{t('procedures.internal')}</StatusPill>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.is_active
                        ? <StatusPill tone="success" dot>{t('common.active')}</StatusPill>
                        : <StatusPill tone="neutral">{t('common.inactive')}</StatusPill>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <IconButton onClick={() => startEdit(p)} label={t('common.edit')} tone="primary">
                          <Pencil className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                        <IconButton onClick={() => onDelete(p)} label={t('common.delete')} tone="danger">
                          <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-base font-semibold mb-5 text-brand-text">
                {editing.id ? t('procedures.editProcedure') : t('procedures.newProcedure')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">
                    {t('procedures.keyLabelPrefix')} <code className="text-xs bg-brand-surface-2 px-1 py-0.5 rounded">cleaning</code>{t('procedures.keyLabelSuffix')}
                  </label>
                  <input
                    type="text"
                    value={editing.key || ''}
                    onChange={e => setEditing({ ...editing, key: e.target.value })}
                    placeholder={t('procedures.keyPlaceholder')}
                    className={`${inputClass} font-mono`}
                  />
                </div>

                {/* Locale tabs */}
                <div>
                  <div className="flex border-b border-brand-border mb-3">
                    {LOCALES.map(l => (
                      <button
                        key={l.code}
                        type="button"
                        onClick={() => setActiveLocale(l.code)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                          activeLocale === l.code
                            ? 'border-brand-primary text-brand-primary'
                            : 'border-transparent text-brand-text-muted hover:text-brand-text'
                        }`}
                      >
                        {l.label}
                        {!editing.name[l.code] && (
                          <span aria-hidden className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-1">
                        {t('common.name')} ({LOCALES.find(l => l.code === activeLocale)?.label}){activeLocale === 'de' && ' *'}
                      </label>
                      <input
                        type="text"
                        value={editing.name[activeLocale] || ''}
                        onChange={e => setEditing({ ...editing, name: { ...editing.name, [activeLocale]: e.target.value } })}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-text mb-1">
                        {t('procedures.descriptionLabel')} ({LOCALES.find(l => l.code === activeLocale)?.label})
                      </label>
                      <textarea
                        rows={2}
                        value={editing.description[activeLocale] || ''}
                        onChange={e => setEditing({ ...editing, description: { ...editing.description, [activeLocale]: e.target.value } })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1">{t('procedures.durationLabel')}</label>
                    <input
                      type="number"
                      min={5}
                      step={5}
                      value={editing.duration ?? 30}
                      onChange={e => setEditing({ ...editing, duration: parseInt(e.target.value) || 0 })}
                      className={`${inputClass} tabular-nums`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1">{t('procedures.colorLabel')}</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PRESET_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditing({ ...editing, color: c })}
                          aria-label={t('procedures.colorAria', { color: c })}
                          className={`w-7 h-7 rounded-md transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
                            editing.color === c
                              ? 'ring-2 ring-offset-2 ring-offset-brand-surface ring-brand-text scale-110'
                              : 'ring-1 ring-brand-border hover:scale-110'
                          }`}
                          style={{ backgroundColor: c }}
                          title={c}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1">{t('procedures.requiredRolesLabel')}</label>
                  <div className="flex flex-wrap gap-2">
                    {ROLE_OPTIONS.map(opt => {
                      const checked = editing.required_roles.includes(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-2 text-sm px-3 py-2 border rounded-md cursor-pointer transition-colors ${
                            checked
                              ? 'border-brand-primary bg-brand-accent/10 text-brand-text'
                              : 'border-brand-border text-brand-text hover:bg-brand-surface-2'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const next = e.target.checked
                                ? Array.from(new Set([...editing.required_roles, opt.value]))
                                : editing.required_roles.filter(r => r !== opt.value);
                              setEditing({ ...editing, required_roles: next });
                            }}
                            className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                          />
                          <span>{t(opt.labelKey)}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-brand-text-muted mt-1.5">
                    {t('procedures.requiredRolesHint')}
                  </p>
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editing.bookable !== false}
                      onChange={e => setEditing({ ...editing, bookable: e.target.checked })}
                      className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                    />
                    <span>{t('procedures.bookableLabel')}</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing.is_active}
                      onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                      className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                    />
                    <span>{t('common.active')}</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 sticky bottom-0 bg-brand-surface border-t border-brand-border">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}>
    {children}
  </th>
);

const IconButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'primary' | 'danger';
}> = ({ children, onClick, label, tone }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400'
      : 'text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10';
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${toneClass}`}
    >
      {children}
    </button>
  );
};

export default ProceduresPage;
